import React, { useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError, mediaUrl } from "../../lib/api";
import { UploadButton } from "../cms/UploadButton";

// Generic list + form editor for collections (products, events).
export const CollectionManager = ({ title, endpoint, items, reload, fields, empty, rowTitle, rowSub, imageKey = "image", testid }) => {
  const [form, setForm] = useState(empty);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/admin/${endpoint}`, form);
      toast.success(form.id ? "Updated — live on the site." : "Added — live on the site.");
      setForm(empty); reload();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const del = async (it) => {
    if (!window.confirm(`Delete "${rowTitle(it)}"?`)) return;
    await api.delete(`/admin/${endpoint}/${it.id}`); toast.success("Deleted."); reload();
  };

  return (
    <div className="grid lg:grid-cols-3 gap-6" data-testid={testid}>
      <form onSubmit={save} className="card-surface p-6 space-y-3 h-fit" data-testid={`${testid}-form`}>
        <h3 className="font-semibold text-brand-ink flex items-center gap-2"><Plus size={16} /> {form.id ? `Edit ${title}` : `Add ${title}`}</h3>
        {fields.map(([k, label, type, opts]) => (
          <div key={k}>
            <label className="text-xs font-medium text-brand-muted">{label}</label>
            {type === "area" ? (
              <textarea rows={3} className="input-field mt-1" value={form[k] ?? ""} onChange={(e) => set(k, e.target.value)} data-testid={`${testid}-${k}`} />
            ) : type === "select" ? (
              <select className="input-field mt-1" value={form[k] ?? ""} onChange={(e) => set(k, e.target.value)} data-testid={`${testid}-${k}`}>
                {opts.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            ) : type === "bool" ? (
              <input type="checkbox" className="ml-2" checked={!!form[k]} onChange={(e) => set(k, e.target.checked)} data-testid={`${testid}-${k}`} />
            ) : (
              <input type={type || "text"} className="input-field mt-1" value={form[k] ?? ""} required={k === fields[0][0]}
                onChange={(e) => set(k, type === "number" ? Number(e.target.value) : e.target.value)} data-testid={`${testid}-${k}`} />
            )}
            {k === imageKey && (
              <div className="flex items-center gap-3 mt-2">
                {form[k] && <img src={mediaUrl(form[k])} alt="" className="w-14 h-14 rounded object-cover border" />}
                <UploadButton accept="image/*" isPublic label="Upload image" testid={`${testid}-upload-image`} onUploaded={(m) => set(k, m.url)} />
              </div>
            )}
          </div>
        ))}
        <button className="btn-primary w-full" data-testid={`${testid}-save`}>{form.id ? "Update" : "Save"}</button>
        {form.id && <button type="button" onClick={() => setForm(empty)} className="w-full text-sm text-brand-muted">Cancel edit</button>}
      </form>

      <div className="lg:col-span-2 card-surface overflow-y-auto max-h-[700px] divide-y divide-brand-line/60">
        {items.map((it) => (
          <div key={it.id} className="flex items-center gap-3 p-3" data-testid={`${testid}-row-${it.id}`}>
            <img src={mediaUrl(it[imageKey])} alt="" className="w-12 h-12 rounded object-cover bg-brand-cream2 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm text-brand-ink truncate">{rowTitle(it)}</p>
              <p className="text-xs text-brand-muted truncate">{rowSub(it)}</p>
            </div>
            <button onClick={() => { setForm({ ...empty, ...it }); window.scrollTo({ top: 0, behavior: "smooth" }); }} className="text-brand-sage" data-testid={`${testid}-edit-${it.id}`}><Pencil size={15} /></button>
            <button onClick={() => del(it)} className="text-red-400" data-testid={`${testid}-delete-${it.id}`}><Trash2 size={16} /></button>
          </div>
        ))}
      </div>
    </div>
  );
};
